import React, {
  Dispatch,
  SetStateAction,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';

import {
  clearMetadataStorage,
  removeExpiredMetadata,
  saveSongsMetadata,
  songsMetadataSelector,
} from '@redux/songStorage';
import { SongWithMetadata } from '@redux/songStorage/types';
import { AppDispatch } from '@redux/store';
import useSelector from '@redux/useSelector';
import { userUuidSelector } from '@redux/user/selectors';
import { fetchBtoBSongMetadata, fetchSongAndNextResources } from '@redux/videoPlayerResources';
import {
  songDetailsRequestSelector,
  videoMetadataRequestSelector,
} from '@redux/videoPlayerResources/selectors';
import { RequestState } from '@redux/videoPlayerResources/types';
import * as Sentry from '@sentry/browser';
import localDB from 'lib/IndexedDB/RessourcesDB';
import { FormattedMessage } from 'react-intl';
import { useDispatch, batch } from 'react-redux';
import { Song } from 'src/@types/api-types';

import { useMediaElementListener } from 'services/hooks/useMediaElementListener';
import { SCOPE } from 'shared-types/constants';
import { PlayerSongDetails } from 'shared-types/player-event';

import { ErrorMessage } from './SharedPlayerArea.style';
import VideoPlayer from './components/VideoPlayer';

const DEFAULT_USER_ID = '123';

const hasError = (songDetailsState: RequestState, videoMetadataState: RequestState) =>
  songDetailsState === RequestState.error || videoMetadataState === RequestState.error;

interface SharedPlayerAreaProps {
  // We pass an object here instead of directly the songId as a string
  // to trigger a rerender when the currentSong is updated, even when
  // the songId is the same: this is useful when playing twice the same
  // song
  song?: { id: string } | null;
  activeVideoElement: HTMLVideoElement | null;
  setInstrumentalVideoElement: Dispatch<SetStateAction<HTMLVideoElement | null>>;
  setVocalGuideVideoElement?: Dispatch<SetStateAction<HTMLVideoElement | null>>;
  handlePlay: () => void;
  handleVideoEnd: () => void;
  scope: SCOPE;
  currentSongDetails?: PlayerSongDetails | null;
  nextSongDetails?: PlayerSongDetails | null;
  playButton?: ReactNode;
  controls?: boolean;
  waitingList: Song[];
  isStorageActivated: boolean;
}

const SharedPlayerArea: React.FunctionComponent<SharedPlayerAreaProps> = ({
  song,
  activeVideoElement,
  setInstrumentalVideoElement,
  setVocalGuideVideoElement,
  handlePlay,
  handleVideoEnd,
  scope,
  currentSongDetails,
  nextSongDetails,
  controls = true,
  waitingList,
  isStorageActivated,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isConnected, setIsConnected] = useState<boolean>(true);
  const songDetailsRequest = useSelector(songDetailsRequestSelector);
  const videoMetadataRequest = useSelector(videoMetadataRequestSelector);
  const userIdFromState = useSelector(userUuidSelector);
  const waitingListMetadata = useSelector(songsMetadataSelector);

  const userId = userIdFromState ?? DEFAULT_USER_ID;

  const songValues = useMemo(() => songDetailsRequest.value, [songDetailsRequest.value]);

  useMediaElementListener(activeVideoElement, 'ended', () => {
    handleVideoEnd();
  });

  const getWaitingListMetadata = useCallback(async () => {
    if (!userId) return;
    const newMetadata: SongWithMetadata[] = [];
    const unsavedSongs = waitingList.filter(
      (song) => !waitingListMetadata.some((metadata) => metadata.id === song.id),
    );

    for (const song of unsavedSongs) {
      const metadata = await fetchBtoBSongMetadata(song, userId);
      newMetadata.push(metadata);
    }

    batch(() => newMetadata.forEach((metadata) => dispatch(saveSongsMetadata(metadata))));
  }, [dispatch, userId, waitingList, waitingListMetadata]);

  useEffect(() => {
    song &&
      userId &&
      dispatch(
        fetchSongAndNextResources({
          songId: song.id,
          scope,
          userId,
          isStorageActivated,
        }),
      );
  }, [dispatch, isStorageActivated, scope, song, userId]);

  useEffect(() => {
    if (isStorageActivated) {
      getWaitingListMetadata();
    } else {
      waitingListMetadata.length && dispatch(clearMetadataStorage());
      localDB.clearFetchedData();
    }
    // eslint-disable-next-line
  }, [getWaitingListMetadata, isStorageActivated, waitingList]);

  useEffect(() => {
    // clearing expired data on launch
    localDB.deleteExpiredData();
    dispatch(removeExpiredMetadata());
  }, [dispatch]);

  const checkInternet = () => {
    if (navigator.onLine) {
      return setIsConnected(true);
    }
    return setIsConnected(false);
  };

  useEffect(() => {
    window.addEventListener('online', checkInternet);
    window.addEventListener('offline', checkInternet);
    return () => {
      window.removeEventListener('online', checkInternet);
      window.removeEventListener('offline', checkInternet);
    };
  }, [isConnected]);

  if (hasError(songDetailsRequest.state, videoMetadataRequest.state)) {
    Sentry.captureMessage(
      `Error while fetching song details or video metadata ${JSON.stringify({
        tags: {
          songId: song?.id,
          userId,
          songDetailsRequestState: songDetailsRequest.state,
          videoMetadataRequestState: videoMetadataRequest.state,
          isConnectedToInternet: isConnected,
        },
      })}`,
    );
    return (
      <ErrorMessage>
        <FormattedMessage id='player-area.error' />
      </ErrorMessage>
    );
  }

  if (song) {
    return (
      <VideoPlayer
        song={song}
        activeVideoElement={activeVideoElement}
        songDetails={songValues}
        setInstrumentalVideoElement={setInstrumentalVideoElement}
        setVocalGuideVideoElement={setVocalGuideVideoElement}
        videoMetadata={videoMetadataRequest.value}
        controls={controls}
        handlePlay={handlePlay}
        scope={scope}
        currentSongDetails={currentSongDetails}
        nextSongDetails={nextSongDetails}
        isStorageActivated={isStorageActivated}
      />
    );
  }

  return null;
};

export default SharedPlayerArea;
